import { useState, useEffect } from 'react';
import { analyticsApi, type FeeAnalytics } from '../lib/analyticsApi';

const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const rupeesShort = (p: number) => {
  const r = p / 100;
  if (r >= 10000000) return '₹' + (r / 10000000).toFixed(1) + ' Cr';
  if (r >= 100000) return '₹' + (r / 100000).toFixed(1) + ' L';
  if (r >= 1000) return '₹' + Math.round(r / 1000) + 'k';
  return '₹' + Math.round(r);
};

export function FeeAnalyticsPage() {
  const [data, setData] = useState<FeeAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    setLoading(true);
    analyticsApi.fees(undefined, months)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [months]);

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (!data) return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load analytics.</div>;

  const s = data.summary;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Finance</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Fee analytics</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Academic year {data.academicYear}. Every figure is computed from the invoice and payment
          ledger — cancelled invoices are excluded.
        </p>
      </div>

      {/* ---- headline numbers ---- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Stat label="Billed" value={rupees(s.billedPaise)} sub={`${s.totalInvoices} invoices`} />
        <Stat label="Collected" value={rupees(s.collectedPaise)} sub={`${s.collectionRatePct}% of billed`} tone="good" />
        <Stat label="Outstanding" value={rupees(s.outstandingPaise)} sub={`${s.studentsWithDues} students`} />
        <Stat label="Overdue" value={rupees(s.overduePaise)} sub="past due date" tone={s.overduePaise > 0 ? 'bad' : undefined} />
      </div>

      {/* ---- collection progress ---- */}
      <div className="card" style={{ padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
          <strong style={{ fontSize: 15 }}>Collection rate</strong>
          <span style={{ fontSize: 22, fontWeight: 700 }}>{s.collectionRatePct}%</span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
            {rupees(s.collectedPaise)} of {rupees(s.billedPaise)}
          </span>
        </div>
        <div style={{ height: 10, borderRadius: 999, background: 'var(--surface-2, #eee)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, Number(s.collectionRatePct))}%`, height: '100%', background: '#1a7f37' }} />
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 12, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
          <span><strong>{s.paidInvoices}</strong> paid in full</span>
          <span><strong>{s.partInvoices}</strong> part-paid</span>
          <span><strong>{s.unpaidInvoices}</strong> nothing paid</span>
          {s.concessionPaise > 0 && <span>Concessions given: <strong>{rupees(s.concessionPaise)}</strong></span>}
          {s.lateFeePaise > 0 && <span>Late fees added: <strong>{rupees(s.lateFeePaise)}</strong></span>}
        </div>
      </div>

      {/* ---- billed vs collected over time ---- */}
      <div className="card" style={{ padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <strong style={{ fontSize: 15, flex: 1 }}>Billed vs collected</strong>
          <select className="input" value={months} onChange={(e) => setMonths(Number(e.target.value))} style={{ width: 'auto' }}>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
        </div>
        <MonthChart data={data.months} />
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: 'var(--ink-2)' }}>
          <Legend colour="#8a6a2e" label="Billed" />
          <Legend colour="#1a7f37" label="Collected" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        {/* ---- ageing ---- */}
        <div className="card" style={{ padding: 18 }}>
          <strong style={{ fontSize: 15 }}>How old is the outstanding money</strong>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '4px 0 12px' }}>
            The same rupee figure means very different things at 10 days and at 6 months.
          </p>
          {data.ageing.map((b) => {
            const max = Math.max(...data.ageing.map((x) => x.outstandingPaise), 1);
            const bad = b.label === '90+ days' || b.label === '61–90 days';
            return (
              <div key={b.label} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', fontSize: 13, marginBottom: 3 }}>
                  <span style={{ flex: 1 }}>{b.label}</span>
                  <span style={{ color: 'var(--ink-3)', marginRight: 10 }}>{b.invoices}</span>
                  <strong>{rupeesShort(b.outstandingPaise)}</strong>
                </div>
                <div style={{ height: 6, background: 'var(--surface-2,#eee)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ width: `${(b.outstandingPaise / max) * 100}%`, height: '100%', background: bad ? '#c1272d' : '#8a6a2e' }} />
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 13 }}>
            Due in the next 30 days: <strong>{rupees(data.expectedNext30Paise)}</strong>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
              Contractually due — not a prediction.
            </div>
          </div>
        </div>

        {/* ---- by fee type ---- */}
        <div className="card" style={{ padding: 18 }}>
          <strong style={{ fontSize: 15 }}>By fee type</strong>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '4px 0 12px' }}>Most outstanding first.</p>
          {data.feeTypes.length === 0 ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No invoices yet.</div>
            : data.feeTypes.map((f) => (
              <div key={f.feeType} style={{ display: 'flex', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                <span style={{ flex: 1 }}>{f.feeType}</span>
                <span style={{ color: 'var(--ink-3)', marginRight: 12 }}>{rupeesShort(f.billedPaise)} billed</span>
                <strong style={{ color: f.outstandingPaise > 0 ? '#c1272d' : '#1a7f37' }}>
                  {rupeesShort(f.outstandingPaise)}
                </strong>
              </div>
            ))}
        </div>
      </div>

      {/* ---- by class ---- */}
      <div className="card" style={{ padding: 18, marginTop: 12 }}>
        <strong style={{ fontSize: 15 }}>Collection by class</strong>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '4px 0 12px' }}>
          Weakest collection first — this is the list to act on.
        </p>
        {data.classes.length === 0 ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No data yet.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--ink-3)', fontSize: 11 }}>
                    <th style={{ padding: '6px 8px' }}>CLASS</th>
                    <th>STUDENTS</th><th>BILLED</th><th>COLLECTED</th><th>OUTSTANDING</th><th style={{ width: 140 }}>RATE</th>
                  </tr>
                </thead>
                <tbody>
                  {data.classes.map((c) => (
                    <tr key={c.classLabel} style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={{ padding: '7px 8px', fontWeight: 500 }}>{c.classLabel}</td>
                      <td>{c.students}</td>
                      <td>{rupeesShort(c.billedPaise)}</td>
                      <td>{rupeesShort(c.collectedPaise)}</td>
                      <td style={{ color: c.outstandingPaise > 0 ? '#c1272d' : 'inherit' }}>{rupeesShort(c.outstandingPaise)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--surface-2,#eee)', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(100, Number(c.collectedPct))}%`, height: '100%',
                              background: Number(c.collectedPct) < 60 ? '#c1272d' : Number(c.collectedPct) < 85 ? '#b26a00' : '#1a7f37' }} />
                          </div>
                          <span style={{ minWidth: 40, textAlign: 'right' }}>{c.collectedPct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }) {
  const colour = tone === 'good' ? '#1a7f37' : tone === 'bad' ? '#c1272d' : 'inherit';
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="eyebrow" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, margin: '4px 0 2px', color: colour }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{sub}</div>}
    </div>
  );
}

function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: colour }} />{label}
    </span>
  );
}

/** Plain SVG bars — no charting dependency, consistent with the rest of the product. */
function MonthChart({ data }: { data: { month: string; billedPaise: number; collectedPaise: number }[] }) {
  if (data.length === 0) return <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No data.</div>;
  const max = Math.max(...data.flatMap((d) => [d.billedPaise, d.collectedPaise]), 1);
  const H = 160, W = Math.max(320, data.length * 54);
  const slot = W / data.length;
  const barW = Math.min(16, slot / 3);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={W} height={H + 26} style={{ display: 'block' }}>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={0} x2={W} y1={H - H * f} y2={H - H * f} stroke="var(--line)" strokeWidth="1" />
        ))}
        {data.map((d, i) => {
          const x = i * slot + slot / 2;
          const bh = (d.billedPaise / max) * H;
          const ch = (d.collectedPaise / max) * H;
          return (
            <g key={d.month}>
              <rect x={x - barW - 2} y={H - bh} width={barW} height={bh} fill="#8a6a2e" opacity="0.75" rx="2">
                <title>{d.month} billed {rupeesShort(d.billedPaise)}</title>
              </rect>
              <rect x={x + 2} y={H - ch} width={barW} height={ch} fill="#1a7f37" rx="2">
                <title>{d.month} collected {rupeesShort(d.collectedPaise)}</title>
              </rect>
              <text x={x} y={H + 16} textAnchor="middle" fontSize="9" fill="var(--ink-3)">
                {d.month.slice(5)}/{d.month.slice(2, 4)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
